import type { Root } from "../../domain/app-types.ts";
import type { Category, Todo } from "../../domain/entities.ts";
import { openCollectionSharing } from "./collection-sharing-controller.ts";
import { openSpaceSharing } from "./space-sharing-controller.ts";
import { openTaskSharing } from "./task-sharing-controller.ts";

export interface SharingWorkflowCapabilities {
  roots(): Root[];
  collections(): Category[];
  task(id: string): Promise<Todo | undefined>;
  sync(): Promise<void>;
  request(path: string, init?: RequestInit): Promise<Response>;
  apiError(response: Response, fallback: string): Promise<string>;
  confirm(title: string, message: string, confirmLabel: string): Promise<boolean>;
  refreshDirectory(): Promise<void>;
  copy(value: string): Promise<void>;
  shareLink(title: string, url: string): Promise<"shared" | "copied" | "cancelled">;
  markTaskShared(id: string, shared: boolean): void;
  origin(): string;
  icon(name: string): string;
  toast(message: string, error?: boolean): void;
  renderShell(): void | Promise<void>;
}

export function createSharingWorkflow(capabilities: SharingWorkflowCapabilities) {
  async function shareSpace(id: string): Promise<void> {
    const root = capabilities.roots().find(item => item.id === id);
    if (!root) return;
    await openSpaceSharing(root, {
      request: capabilities.request,
      apiError: capabilities.apiError,
      confirmRemove: email => capabilities.confirm("Remove member?", `Remove space membership for ${email}? Separate collection or task grants may still provide access.`, "Remove access"),
      refreshDirectory: capabilities.refreshDirectory,
      copy: capabilities.copy,
      origin: capabilities.origin,
      icon: capabilities.icon,
      toast: capabilities.toast,
    });
  }

  async function shareTask(id: string): Promise<void> {
    await capabilities.sync();
    const task = await capabilities.task(id);
    if (!task) return;
    await openTaskSharing(task, {
      request: capabilities.request,
      apiError: capabilities.apiError,
      confirm: capabilities.confirm,
      markShared: shared => capabilities.markTaskShared(id, shared),
      shareLink: capabilities.shareLink,
      origin: capabilities.origin,
      icon: capabilities.icon,
      toast: capabilities.toast,
      renderShell: capabilities.renderShell,
    });
  }

  async function shareCollection(id: string): Promise<void> {
    await capabilities.sync();
    const collection = capabilities.collections().find(item => item.id === id);
    if (!collection) return;
    await openCollectionSharing(collection, {
      request: capabilities.request,
      apiError: capabilities.apiError,
      confirmRemove: email => capabilities.confirm("Remove collection access?", `Remove the direct collection grant for ${email}? Space membership or separate task grants may still provide access.`, "Remove access"),
      refreshDirectory: capabilities.refreshDirectory,
      icon: capabilities.icon,
      toast: capabilities.toast,
    });
  }

  return { shareSpace, shareTask, shareCollection };
}
